import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Lock, Mail, Dumbbell } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatApiError } from '@/components/ProtectedRoute';
import { PageMeta } from '@/components/PageMeta';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();

  if (!loading && user) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success('Connecté !');
      navigate('/admin');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark relative overflow-hidden p-4">
      <PageMeta title="Connexion admin" description="Espace administration" />
      <div className="absolute inset-0 mesh-gradient opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -right-20 w-[40vw] h-[40vw] bg-primary/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-20 w-[30vw] h-[30vw] bg-primary-glow/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-md p-8 rounded-2xl bg-white/[0.06] backdrop-blur-xl border border-white/20 text-white shadow-2xl" data-testid="admin-login-card">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-primary/20 border border-primary-glow/40 flex items-center justify-center">
            <Dumbbell className="h-5 w-5 text-primary-glow" />
          </div>
          <div>
            <h1 className="font-heading text-xl tracking-wider">BACK-OFFICE</h1>
            <p className="text-xs text-white/50 uppercase tracking-widest">CrossFit Saint-Chamond</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-white/80 text-xs tracking-widest uppercase mb-2 block" htmlFor="email">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-primary-glow/60 focus:ring-primary-glow/30 h-12 rounded-xl"
                placeholder="crossfit.saintchamond@gmail.com"
                autoComplete="email"
                data-testid="admin-login-email"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/80 text-xs tracking-widest uppercase mb-2 block" htmlFor="password">
              Mot de passe
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 bg-white/5 border-white/20 text-white placeholder:text-white/30 focus:border-primary-glow/60 focus:ring-primary-glow/30 h-12 rounded-xl"
                placeholder="••••••••"
                autoComplete="current-password"
                data-testid="admin-login-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="btn-shine w-full bg-primary hover:bg-primary-light text-white font-heading tracking-wider text-sm py-7 rounded-xl glow-primary disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                CONNEXION...
              </>
            ) : (
              'SE CONNECTER'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
